import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';

// Define types
export interface CartItem {
  id: string;
  title: string;
  artist: string;
  url: string;
  coverArt?: string;
  year?: string;
  genre?: string[];
  price?: number;
  isAcetate?: boolean;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  cartCount: number;
  // Inquiry panel visibility lives here so a License button anywhere in the
  // tree can open the panel directly (the old cart metaphor hid it behind
  // the navbar icon, which supervisors never found).
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const defaultContext: CartContextType = {
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  clearCart: () => {},
  isInCart: () => false,
  cartCount: 0,
  isCartOpen: false,
  openCart: () => {},
  closeCart: () => {},
  toggleCart: () => {},
};

// Validate cart item has required fields
function isValidCartItem(item: unknown): item is CartItem {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.artist === 'string' &&
    typeof obj.url === 'string'
  );
}

// Validate and filter cart data from localStorage
function validateCartData(data: unknown): CartItem[] {
  if (!Array.isArray(data)) return [];
  return data.filter(isValidCartItem);
}

const CartContext = createContext<CartContextType>(defaultContext);

// Provider component
export const CartProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    // Lazy initialization - load from localStorage once on mount
    const savedCart = localStorage.getItem('vintageMusicCart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        // Validate parsed data to ensure it matches expected schema
        return validateCartData(parsed);
      } catch {
        // Invalid JSON - clear corrupted data
        localStorage.removeItem('vintageMusicCart');
      }
    }
    return [];
  });

  // Derive cartCount from cartItems - no separate state needed
  const cartCount = cartItems.length;

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vintageMusicCart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Add item to cart - memoized to prevent unnecessary rerenders
  const addToCart = useCallback((item: CartItem) => {
    setCartItems(prev => {
      // Check if item is already in cart
      if (prev.some(cartItem => cartItem.id === item.id)) {
        return prev;
      }
      return [...prev, item];
    });
  }, []);

  // Remove item from cart - memoized
  const removeFromCart = useCallback((id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  }, []);

  // Clear cart - memoized
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // Check if item is in cart - memoized
  const isInCart = useCallback((id: string) => {
    return cartItems.some(item => item.id === id);
  }, [cartItems]);

  const [isCartOpen, setIsCartOpen] = useState(false);
  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);

  // Memoize context value to prevent unnecessary rerenders
  const value = useMemo(() => ({
    cartItems,
    addToCart,
    removeFromCart,
    clearCart,
    isInCart,
    cartCount,
    isCartOpen,
    openCart,
    closeCart,
    toggleCart,
  }), [cartItems, addToCart, removeFromCart, clearCart, isInCart, cartCount, isCartOpen, openCart, closeCart, toggleCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;